// =============================================================================
// Overdue (§7) — "the reminder mechanism".
//
// There are no automated reminders in v1 (§1 out of scope), so this screen IS
// how a late book comes back. That single fact drives the whole design:
//
// It is a CALL LIST, not a report. So it groups by MEMBER, not by book. A child
// with three books out is one phone call, not three rows — and a list that
// makes staff ring the same parent three times is a list they stop using.
//
// Each group leads with a full-width tap-to-call button, because this screen is
// used on a phone with the parent's number in front of you.
// =============================================================================

import { el, mount, formatDate } from '../lib/dom.js';
import { T } from '../lib/strings.js';
import { spinner } from '../lib/loading.js';
import { listOverdue } from '../lib/api.js';

export function overdueScreen(root) {
  let groups = [];
  let loading = true;
  let error = null;

  const view = el('div', { class: 'screen' });
  mount(root, view);

  async function load() {
    loading = true; error = null; render();
    try {
      groups = groupByMember(await listOverdue());
    } catch (err) {
      error = err.message;
    }
    loading = false; render();
  }

  // One entry per member, worst offender first, and within a member the most
  // overdue book first.
  function groupByMember(rows) {
    const byMember = new Map();
    for (const r of rows) {
      if (!byMember.has(r.member_id)) {
        byMember.set(r.member_id, {
          member_id: r.member_id,
          member_name: r.member_name,
          member_code: r.member_code,
          guardian_name: r.guardian_name,
          phones: [r.guardian_phone, r.guardian_phone_2].filter(Boolean),
          loans: [],
        });
      }
      byMember.get(r.member_id).loans.push(r);
    }
    const out = [...byMember.values()];
    for (const g of out) {
      g.loans.sort((a, b) => b.days_overdue - a.days_overdue);
      g.worst = g.loans[0].days_overdue;
    }
    return out.sort((a, b) => b.worst - a.worst || b.loans.length - a.loans.length);
  }

  function render() {
    const bookCount = groups.reduce((n, g) => n + g.loans.length, 0);

    mount(view,
      el('div', { class: 'screen-head' },
        el('h1', { class: 'screen-title' }, T.overdueTitle),
        el('button', { class: 'btn btn-sm', onClick: load }, T.refresh),
      ),

      loading ? spinner() : null,
      error ? el('p', { class: 'error' }, error) : null,

      !loading && !error && groups.length === 0
        // Worth celebrating rather than showing an empty table — this is the
        // state the school wants to be in.
        ? el('div', { class: 'banner banner-ok' },
            el('strong', { class: 'banner-title' }, T.nothingOverdue),
            el('p', { class: 'banner-reason' }, T.nothingOverdueHint))
        : null,

      !loading && !error && groups.length > 0
        ? el('p', { class: 'slots' }, T.overdueSummary(bookCount, groups.length))
        : null,

      ...groups.map(groupCard),
    );
  }

  function groupCard(g) {
    return el('section', { class: 'card card-warn overdue-card' },
      el('div', { class: 'overdue-head' },
        el('div', {},
          el('a', { class: 'overdue-name', href: `#/members/${g.member_id}` }, g.member_name),
          el('p', { class: 'muted' },
            [g.member_code, g.guardian_name].filter(Boolean).join(' · ')),
        ),
        el('span', { class: 'pill pill-danger' }, T.daysOverdue(g.worst)),
      ),

      // The point of the screen. A real <a href="tel:"> so it dials on a phone
      // and is still a normal link on a desktop.
      ...g.phones.map((phone, i) => el('a', {
        class: `btn ${i === 0 ? 'btn-primary' : ''} btn-large call-btn`,
        href: `tel:${phone.replace(/\s/g, '')}`,
      }, i === 0 ? T.callGuardian(phone) : T.callAlternate(phone))),

      g.phones.length === 0
        ? el('p', { class: 'muted' }, T.noPhoneOnFile)
        : null,

      el('div', { class: 'overdue-books' }, ...g.loans.map((l) => el('div', { class: 'loan-row' },
        el('span', { class: 'loan-title' }, l.title),
        el('span', { class: 'basket-code' }, l.copy_code),
        el('span', { class: 'pill pill-danger' }, T.dueWas(formatDate(l.due_at))),
      ))),
    );
  }

  load();
  return { destroy: () => {} };
}
